import React, { memo, useEffect, useState, useRef, FC } from 'react';
import groupBy from 'lodash/groupBy';
import styles from './Header.module.scss';
import classNames from 'classnames';
import { Dropdown, Modal } from 'antd';
import { languageDict, languageMenuItems } from '@/utils/constants';
import MenuDrawer from '@/components/MenuDrawer/MenuDrawer';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { HeaderProps } from './Header.types';
import HeaderLogoBlock from './HeaderLogoBlock';
import HeaderMegaMenu from './HeaderMegaMenu';
import { useHeader } from '@/hooks/use-header';
import dayjs from 'dayjs';

export const Header: FC<HeaderProps> = memo(props => {
  const topNav = useHeader();
  const router = useRouter();
  const { language, articleData, header } = props;
  /** logo information */
  const logoData = topNav?.logo ?? {};
  /** navigation information */
  const navigationSectionsData = topNav?.navigation_sections ?? [];

  const [open, setOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenChange = (flag: boolean) => {
    setOpen(flag);
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = (language: string) => {
    setIsModalOpen(false);
    language === 'en'
      ? router.push({
          pathname: '/en/blog'
        })
      : router.push({
          pathname: '/zh/blog'
        });
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const changeLanguage = (e: any) => {
    setOpen(false);
    if (
      (router.pathname == '/blog/[category]/[article]' &&
        articleData?.languages == 'CH_Only' &&
        e.key == 'en') ||
      (router.pathname == '/blog/[category]/[article]' &&
        articleData?.languages == 'EN_Only' &&
        e.key == 'zh') ||
      (router.pathname.includes('guide') && e.key === 'en')
    ) {
      showModal();
      return;
    }
    const safeUrl = `/${e.key}${location.pathname.replace(/^\/(zh|en)\b/, '')}${location.search}`;
    router.push(safeUrl);
  };

  const menuRef = useRef<any>();
  const openDrawer = () => {
    menuRef.current.openMenu();
  };
  const closeDrawer = () => {
    menuRef.current.closeMenu();
  };

  const [residueTime, setResidueTime] = useState<any>('');

  const majorLinks: any[] = [];
  const titleLinks: any[] = [];
  const promotionsData: any[] = [];
  const calloutBlocks: any = [];

  navigationSectionsData?.forEach((x: any) => {
    if (x?.major_grouping) {
      const { modular_blocks, ...rest } = x.major_grouping ?? {};

      const subLinks: any[] = [];
      const dropdownLinks: any[] = [];
      let promotedSection: any = null;

      modular_blocks?.forEach((block: any) => {
        if (block?.Sub_grouping) {
          subLinks.push(block?.Sub_grouping);
        }

        if (block?.dropdown_link) {
          dropdownLinks.push(block?.dropdown_link);
        }

        if (block?.Promoted_section) {
          promotedSection = block?.Promoted_section;
        }
      });

      majorLinks.push({
        ...rest,
        subLinks,
        dropdownLinks,
        promotedSection
      });
    }

    if (x?.title_link) {
      titleLinks.push(x.title_link);
    }

    if (x?.ecom_promotion_ref) {
      const promotion =
        x?.ecom_promotion_ref?.ecommerce_promotion_reference?.[0]?.promotion?.[0]?.promotion;
      promotion && promotionsData.push(promotion);
    }

    if (x?.callout_block) {
      calloutBlocks.push(x.callout_block);
    }
  });

  const endTime = new Date(promotionsData?.[0]?.end_time).getTime();

  const productLinks = majorLinks?.length > 0 ? majorLinks[0] : [];
  const productMenuData = groupBy(productLinks.subLinks, 'sub_grouping_title');

  // split
  const splitArray = (array: any[]) => {
    const clone = [...array];
    const newArray = clone.splice(0, Math.ceil(clone.length / 2));
    return [newArray, clone];
  };

  useEffect(() => {
    if (language.origin === 'zh') {
      setResidueTime(`優惠至: ${dayjs(endTime).format('YYYY年MM月DD日')}`);
    } else if (language.origin === 'en') {
      setResidueTime(`Offer ends on ${dayjs(endTime).format('DD MMM YYYY')}`);
    }
  }, [endTime, language.origin]);

  const headerMenuData = (type: String) => {
    let menuArrs = [];

    if (topNav) {
      for (let m of topNav?.navigation_sections) {
        if (type == 'major_grouping' && m?.major_grouping) {
          if (m?.major_grouping?.modular_blocks.length > 0) {
            return;
          } else {
            menuArrs.push(
              <span
                key={m?.major_grouping?.major_group_link?.title}
                onClick={() =>
                  handleGA(
                    m?.major_grouping?.major_group_link?.title,
                    m?.major_grouping?.major_group_link?.href
                  )
                }
              >
                <a href={m?.major_grouping?.major_group_link?.href}>
                  {m?.major_grouping?.major_group_link?.title}
                </a>
              </span>
            );
          }
        } else if (type == 'title_link' && m?.title_link) {
          menuArrs.push(
            <span
              key={m?.title_link?.link?.title}
              onClick={() => handleGA(m?.title_link?.link?.title, m?.title_link?.link?.href)}
            >
              <a
                href={m?.title_link?.link?.href}
                target={m?.title_link?.open_in_new_tab ? '_blank' : ''}
              >
                {m?.title_link?.link?.title}
              </a>
            </span>
          );
        }
      }
    }
    return menuArrs;
  };

  const handleGA = (title: any, href: any) => {
    window?.dataLayer?.push({
      event: 'menuBar',
      buttonTitle: title,
      destinationUrl: href,
      pageURL: window.location.href,
      productName: title,
      componentName: 'header menu'
    });
  };

  const footerData = header.footer || [];
  // const navigationButtonData = headerData[0]?.icon_navigation_links || [];
  const navigationButtonData = topNav?.icon_navigation_links || [];

  const [isClicked, setIsClicked] = useState(false);
  const handleClicked = () => {
    setIsClicked(!isClicked);
  };

  const handleMenuClick = () => {
    handleClicked();
    isClicked ? closeDrawer() : openDrawer();
  };

  return (
    <header className={classNames(styles.Header, { [styles.Header_Open]: isClicked })}>
      <div className={styles.Header_Top}>
        <div className={styles.Align_Left}>
          <div
            className={isClicked ? styles.Test_Wrap_Change : styles.Test_Wrap}
            onClick={handleMenuClick}
          >
            <span
              className={classNames(styles.Test_Up, router.pathname === '/blog' && styles.BG_White)}
            ></span>
            <span
              className={classNames(
                styles.Test_Mid,
                router.pathname === '/blog' && styles.BG_White
              )}
            ></span>
            <span
              className={classNames(
                styles.Test_Bot,
                router.pathname === '/blog' && styles.BG_White
              )}
            ></span>
          </div>
        </div>
        <div className={styles.Header_Container}>
          <div className={styles.Header_Left_Content}>
            <HeaderLogoBlock logo={logoData} />
            <div className={styles.Header_Menu}>
              <HeaderMegaMenu
                majorLinks={majorLinks}
                language={language}
                promotions={promotionsData}
                productMenuData={productMenuData}
                calloutBlocks={calloutBlocks}
              />
            </div>
            <div className={styles.Divide}></div>
            <div className={styles.Company_Info}>{headerMenuData('title_link')}</div>
          </div>

          <div className={styles.Header_Right_Content}>
            <div
              className={classNames(
                styles.Navigation_Bar,
                isClicked ? styles.Navigation_Bar_hide : styles.Navigation_Bar_show
              )}
            >
              {navigationButtonData.length > 0 &&
                navigationButtonData.map((item: any, index: number) => {
                  const buttonStyle = item.icon_style || '';
                  const buttonTitle = item.icon_link.title || '';
                  const buttonUid = item._metadata?.uid || '';
                  const buttonUrl = item.icon_link.href || '';
                  const openInNewTab = item.open_in_new_tab || null;
                  const iconData = item.icon || {};
                  const iconUrl = iconData?.url || '';
                  const childrenData = item.children || [];
                  let filteredChildren: [] = [];
                  if (childrenData.length > 0) {
                    filteredChildren = childrenData.filter(
                      (item: any) => item.child_link.title.trim() !== ''
                    );
                  }

                  if (buttonStyle === 'Primary') {
                    const dropdownRender = (menu: any) => {
                      return (
                        <ul
                          className={classNames(
                            styles.Children_List,
                            language.origin === 'zh'
                              ? styles.Children_List_ZH
                              : styles.Children_List_EN
                          )}
                        >
                          {filteredChildren.length > 0 &&
                            filteredChildren.map((child: any, k: any) => {
                              const childrenTitle = child.child_link?.title || '';
                              const childrenUrl = child.child_link?.href || '';
                              const childrenUid = child._metadata?.uid || '';
                              const childOpenInNewTab = child.open_in_new_tab || null;
                              const childrenIconData = child.icon || {};
                              const childrenIconUrl = childrenIconData?.url || '';
                              const childrenIconActiveData = child.icon_active || {};
                              const childrenIconActiveUrl = childrenIconActiveData?.url || '';

                              return (
                                <li key={childrenUid} className={styles.Child_Item}>
                                  <a href={childrenUrl} target={childOpenInNewTab ? '_blank' : ''}>
                                    {childrenIconUrl && childrenIconActiveUrl ? (
                                      <>
                                        <Image
                                          src={childrenIconUrl}
                                          className={styles.Child_Icon}
                                          alt={''}
                                          width={25}
                                          height={25}
                                        />
                                        <Image
                                          src={childrenIconActiveUrl}
                                          className={styles.Child_Icon_Active}
                                          alt={''}
                                          width={25}
                                          height={25}
                                        />
                                      </>
                                    ) : childrenIconUrl && !childrenIconActiveUrl ? (
                                      <>
                                        <Image
                                          src={childrenIconUrl}
                                          className={styles.Child_Icon_Usual}
                                          alt={''}
                                          width={25}
                                          height={25}
                                        />
                                      </>
                                    ) : (
                                      ''
                                    )}
                                    <span>{childrenTitle}</span>
                                  </a>
                                </li>
                              );
                            })}
                        </ul>
                      );
                    };

                    if (filteredChildren.length > 0) {
                      return (
                        <Dropdown
                          key={buttonUid}
                          className={styles.Primary_Button}
                          arrow
                          placement="bottomRight"
                          dropdownRender={dropdownRender}
                        >
                          {buttonUrl && buttonTitle ? (
                            <a
                              href={
                                buttonUrl.includes('#')
                                  ? `https://www.fwd.com.hk/online-insurance/${buttonUrl}`
                                  : buttonUrl
                              }
                              target={openInNewTab ? '_blank' : ''}
                              onClick={() => handleGA(buttonTitle, buttonUrl)}
                            >
                              {buttonTitle}
                            </a>
                          ) : !buttonUrl && buttonTitle ? (
                            <span>{buttonTitle}</span>
                          ) : (
                            <span></span>
                          )}
                        </Dropdown>
                      );
                    } else {
                      return (
                        <Dropdown
                          key={buttonUid}
                          className={styles.Primary_Button}
                          arrow
                          placement="bottomRight"
                          dropdownRender={dropdownRender}
                          disabled
                        >
                          {buttonUrl && buttonTitle ? (
                            <a
                              href={
                                buttonUrl.includes('#')
                                  ? `https://www.fwd.com.hk/online-insurance/${buttonUrl}`
                                  : buttonUrl
                              }
                              target={openInNewTab ? '_blank' : ''}
                              onClick={() => handleGA(buttonTitle, buttonUrl)}
                            >
                              {buttonTitle}
                            </a>
                          ) : !buttonUrl && buttonTitle ? (
                            <span>{buttonTitle}</span>
                          ) : (
                            <span></span>
                          )}
                        </Dropdown>
                      );
                    }
                  }
                  if (buttonStyle === 'Secondary') {
                    const dropdownRender = (menu: any) => {
                      return (
                        <ul
                          className={classNames(
                            styles.Children_List,
                            language.origin === 'zh'
                              ? styles.Children_List_ZH
                              : styles.Children_List_EN
                          )}
                        >
                          {filteredChildren.length > 0 &&
                            filteredChildren.map((child: any, k: any) => {
                              const childrenTitle = child.child_link?.title || '';
                              const childrenUrl = child.child_link?.href || '';
                              const childrenUid = child._metadata?.uid || '';
                              const childOpenInNewTab = child.open_in_new_tab || null;
                              const childrenIconData = child.icon || {};
                              const childrenIconUrl = childrenIconData?.url || '';
                              const childrenIconActiveData = child.icon_active || {};
                              const childrenIconActiveUrl = childrenIconActiveData?.url || '';

                              return (
                                <li key={childrenUid} className={styles.Child_Item}>
                                  <a href={childrenUrl} target={childOpenInNewTab ? '_blank' : ''}>
                                    {childrenIconUrl && childrenIconActiveUrl ? (
                                      <>
                                        <Image
                                          src={childrenIconUrl}
                                          className={styles.Child_Icon}
                                          alt={''}
                                          width={25}
                                          height={25}
                                        />
                                        <Image
                                          src={childrenIconActiveUrl}
                                          className={styles.Child_Icon_Active}
                                          alt={''}
                                          width={25}
                                          height={25}
                                        />
                                      </>
                                    ) : childrenIconUrl && !childrenIconActiveUrl ? (
                                      <>
                                        <Image
                                          src={childrenIconUrl}
                                          className={styles.Child_Icon_Usual}
                                          alt={''}
                                          width={25}
                                          height={25}
                                        />
                                      </>
                                    ) : (
                                      ''
                                    )}
                                    <span>{childrenTitle}</span>
                                  </a>
                                </li>
                              );
                            })}
                        </ul>
                      );
                    };

                    if (filteredChildren.length > 0) {
                      return (
                        <Dropdown
                          key={buttonUid}
                          className={styles.Secondary_Button}
                          arrow
                          placement="bottomRight"
                          dropdownRender={dropdownRender}
                        >
                          {buttonUrl && buttonTitle ? (
                            <a
                              href={
                                buttonUrl.includes('#')
                                  ? `https://www.fwd.com.hk/online-insurance/${buttonUrl}`
                                  : buttonUrl
                              }
                              target={openInNewTab ? '_blank' : ''}
                              onClick={() => handleGA(buttonTitle, buttonUrl)}
                            >
                              {buttonTitle}
                            </a>
                          ) : !buttonUrl && buttonTitle ? (
                            <span>{buttonTitle}</span>
                          ) : (
                            <span></span>
                          )}
                        </Dropdown>
                      );
                    } else {
                      return (
                        <Dropdown
                          key={buttonUid}
                          className={styles.Secondary_Button}
                          arrow
                          placement="bottomRight"
                          dropdownRender={dropdownRender}
                          disabled
                        >
                          {buttonUrl && buttonTitle ? (
                            <a
                              href={
                                buttonUrl.includes('#')
                                  ? `https://www.fwd.com.hk/online-insurance/${buttonUrl}`
                                  : buttonUrl
                              }
                              target={openInNewTab ? '_blank' : ''}
                              onClick={() => handleGA(buttonTitle, buttonUrl)}
                            >
                              {buttonTitle}
                            </a>
                          ) : !buttonUrl && buttonTitle ? (
                            <span>{}</span>
                          ) : (
                            <span></span>
                          )}
                        </Dropdown>
                      );
                    }
                  }
                  if (buttonStyle === 'Icon') {
                    const dropdownRender = (menu: any) => {
                      return (
                        <ul
                          className={classNames(
                            styles.Children_List,
                            language.origin === 'zh'
                              ? styles.Children_List_ZH
                              : styles.Children_List_EN
                          )}
                        >
                          {filteredChildren.length > 0 &&
                            filteredChildren.map((child: any, k: any) => {
                              const childrenTitle = child.child_link?.title || '';
                              const childrenUrl = child.child_link?.href || '';
                              const childrenUid = child._metadata?.uid || '';
                              const childOpenInNewTab = child.open_in_new_tab || null;
                              const childrenIconData = child.icon || {};
                              const childrenIconUrl = childrenIconData?.url || '';
                              const childrenIconActiveData = child.icon_active || {};
                              const childrenIconActiveUrl = childrenIconActiveData?.url || '';

                              return (
                                <li key={childrenUid} className={styles.Child_Item}>
                                  <a href={childrenUrl} target={childOpenInNewTab ? '_blank' : ''}>
                                    {childrenIconUrl && childrenIconActiveUrl ? (
                                      <>
                                        <Image
                                          src={childrenIconUrl}
                                          className={styles.Child_Icon}
                                          alt={''}
                                          width={25}
                                          height={25}
                                        />
                                        <Image
                                          src={childrenIconActiveUrl}
                                          className={styles.Child_Icon_Active}
                                          alt={''}
                                          width={25}
                                          height={25}
                                        />
                                      </>
                                    ) : childrenIconUrl && !childrenIconActiveUrl ? (
                                      <>
                                        <Image
                                          src={childrenIconUrl}
                                          className={styles.Child_Icon_Usual}
                                          alt={''}
                                          width={25}
                                          height={25}
                                        />
                                      </>
                                    ) : (
                                      ''
                                    )}
                                    <span>{childrenTitle}</span>
                                  </a>
                                </li>
                              );
                            })}
                        </ul>
                      );
                    };

                    if (filteredChildren.length > 0) {
                      return (
                        <Dropdown
                          key={buttonUid}
                          className={styles.Icon_Button}
                          arrow
                          placement="bottomRight"
                          dropdownRender={dropdownRender}
                        >
                          {buttonUrl && iconUrl ? (
                            <a
                              href={
                                buttonUrl.includes('#')
                                  ? `https://www.fwd.com.hk/online-insurance/${buttonUrl}`
                                  : buttonUrl
                              }
                              target={openInNewTab ? '_blank' : ''}
                              onClick={() => handleGA('chatbot_button', buttonUrl)}
                            >
                              <Image
                                src={iconUrl}
                                alt={''}
                                width={32}
                                height={32}
                                className={styles.Icon_Image}
                              />
                            </a>
                          ) : !buttonUrl && iconUrl ? (
                            <Image
                              src={iconUrl}
                              alt={''}
                              width={32}
                              height={32}
                              className={styles.Icon_Image}
                            />
                          ) : (
                            <span></span>
                          )}
                        </Dropdown>
                      );
                    } else {
                      return (
                        <Dropdown
                          key={buttonUid}
                          className={styles.Icon_Button}
                          arrow
                          placement="bottomRight"
                          disabled
                        >
                          {buttonUrl && iconUrl ? (
                            <a
                              href={
                                buttonUrl.includes('#')
                                  ? `https://www.fwd.com.hk/online-insurance/${buttonUrl}`
                                  : buttonUrl
                              }
                              target={openInNewTab ? '_blank' : ''}
                              onClick={() => handleGA('chatbot_button', buttonUrl)}
                            >
                              <Image
                                src={iconUrl}
                                alt={''}
                                width={32}
                                height={32}
                                className={styles.Icon_Image}
                              />
                            </a>
                          ) : !buttonUrl && iconUrl ? (
                            <Image
                              src={iconUrl}
                              alt={''}
                              width={32}
                              height={32}
                              className={styles.Icon_Image}
                            />
                          ) : (
                            <span></span>
                          )}
                        </Dropdown>
                      );
                    }
                  }
                })}
            </div>
            <Dropdown
              trigger={['click']}
              onOpenChange={handleOpenChange}
              open={open}
              dropdownRender={menu => (
                <ul
                  className={classNames(
                    styles.Language_Options_Wrapper,
                    language.origin === 'zh'
                      ? styles.Language_Options_Wrapper_ZH
                      : styles.Language_Options_Wrapper_EN
                  )}
                >
                  {languageMenuItems.map((item: any) => (
                    <li
                      className={classNames(
                        styles.Language_Item,
                        languageDict[language.origin] === item.label ? styles.Selected_Lan : ''
                      )}
                      key={item.key}
                      onClick={() => changeLanguage(item)}
                    >
                      <span className={styles.Language_Label}>{item.label}</span>
                      {languageDict[language.origin] === item.label ? (
                        <Image
                          src="/blog/icon/Tick.svg"
                          className={styles.Tick}
                          alt={''}
                          width={11}
                          height={9}
                        />
                      ) : (
                        ''
                      )}
                      {languageDict[language.origin] === item.label ? (
                        <Image
                          src="/blog/icon/TickWhite.svg"
                          className={styles.Tick_White}
                          alt={''}
                          width={11}
                          height={9}
                        />
                      ) : (
                        ''
                      )}
                    </li>
                  ))}
                </ul>
              )}
            >
              <span
                className={classNames(
                  styles.Language_Menu,
                  isClicked ? styles.Language_Menu_show : styles.Language_Menu_hide
                )}
              >
                <Image
                  src="/blog/icon/Earth.svg"
                  className={styles.Earth_Icon}
                  alt={''}
                  width={16}
                  height={16}
                  style={{ display: isClicked ? 'inline-block' : 'none' }}
                />
                <span>{languageDict[language.value]}</span>
                <Image src="/blog/icon/Sort.svg" alt={''} width={16} height={16} />
              </span>
            </Dropdown>
          </div>
        </div>
      </div>
      <MenuDrawer
        ref={menuRef}
        setIsClicked={setIsClicked}
        header={topNav}
        footers={footerData}
        language={language}
        articleData={articleData}
        majorLinks={majorLinks}
      />

      <Modal
        className={classNames(
          styles.Header_Modal,
          language.value === 'en' ? styles.Header_Modal_EN : styles.Header_Modal_ZH
        )}
        width={632}
        centered
        open={isModalOpen}
        onOk={() => handleOk(language.value)}
        onCancel={handleCancel}
        okText={language.value == 'en' ? 'Explore' : '瀏覽更多'}
        maskClosable={false}
        cancelText={language?.value == 'en' ? 'Stay on page' : '繼續瀏覽'}
      >
        <h3 className={styles.Modal_Title}>
          {language?.value == 'en'
            ? 'This content might not be in your language'
            : '此內容可能沒有您想要的語言版本'}
        </h3>
        <p className={styles.Modal_Text}>
          {language?.value == 'en'
            ? 'We are currently working to have this content in your language. You can choose to stay on this page, or proceed to explore other relevant contents.'
            : '我們正在努力提供您的語言版本。您可以選擇留在此頁或繼續探索其他相關內容。'}
        </p>
      </Modal>
    </header>
  );
});
